import React, { useState } from 'react';
import type { Config, LayoutItem, BreakpointName } from '../Config/config';
import { getComponentMetadata, getAllComponents } from './ComponentRegistry';
import GridSimulator from './GridSimulator';
import { useModulesApi } from '../Hooks/useModulesApi';
import { ModulesApiService } from '../Services/ModulesApiService';
import { resetToOriginalConfig, resyncFromLocal } from './ApiDataLoader';

export const ConfigEditor: React.FC<{
  config: Config;
  onConfigChange: (newConfig: Partial<Config>) => void;
  currentBreakpoint: BreakpointName;
  selectedComponent?: LayoutItem | null;
  selectedPosition?: { colStart: number; colEnd: number; rowStart: number; rowEnd: number } | null;
  onApplyPosition?: (componentId: string) => void;
  onClose?: () => void;
  onSelectComponentForMove?: (componentId: string) => void;
  isSelecting?: boolean;
  currentPageUrl?: string;
  onNavigateToPage?: (url: string) => void;
  onSimulationChange?: (simulatedWidth: number | undefined) => void;
}> = ({ config, onConfigChange, currentBreakpoint, selectedComponent, selectedPosition, onApplyPosition, onClose, onSelectComponentForMove, isSelecting = false, currentPageUrl = '/', onNavigateToPage, onSimulationChange }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState<'layout' | 'design' | 'pages' | 'api'>('layout');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showAdvancedSave, setShowAdvancedSave] = useState(false);
  const [showAdvancedGridParams, setShowAdvancedGridParams] = useState<{ [pageUrl: string]: boolean }>({});
  const [showComponentModal, setShowComponentModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  
  const currentPage = config.PagesList.find(p => p.pageUrl === currentPageUrl);
  
  // Hook pour gérer l'API de modules
  const { compareModules, syncModules, compareInstances, syncInstances, isLoading, error: apiError } = useModulesApi(config.api);

  const handleColorChange = (key: keyof Config, value: string) => {
    onConfigChange({ [key]: value });
  };

  const handleModuleParamChange = (moduleName: string, paramKey: string, value: unknown) => {
    const currentModuleParams = config.componentParams[moduleName] || {};
    const newParams = {
      ...config.componentParams,
      [moduleName]: {
        ...currentModuleParams,
        [paramKey]: value
      }
    };
    onConfigChange({ componentParams: newParams });
  };

  const handleInstanceParamChange = (itemId: string, paramKey: string, value: unknown) => {
    const currentPage = config.PagesList.find(p => p.pageUrl === currentPageUrl);
    
    if (currentPage?.gridLayout) {
      // Modifier les paramètres dans la grille personnalisée de la page
      const newPageItems = currentPage.gridLayout.items.map(item => {
        if (item.id === itemId) {
          return {
            ...item,
            props: {
              ...item.props,
              [paramKey]: value
            }
          };
        }
        return item;
      });
      
      const newPages = config.PagesList.map(p => 
        p.pageUrl === currentPageUrl 
          ? { ...p, gridLayout: { ...p.gridLayout, items: newPageItems, breakpoints: p.gridLayout?.breakpoints ?? config.gridLayout.breakpoints } }
          : p
      );
      
      onConfigChange({ PagesList: newPages });
    } else {
      // Modifier les paramètres dans la grille globale
      const newItems = config.gridLayout.items.map(item => {
        if (item.id === itemId) {
          return {
            ...item,
            props: {
              ...item.props,
              [paramKey]: value
            }
          };
        }
        return item;
      });
      
      onConfigChange({
        gridLayout: {
          ...config.gridLayout,
          items: newItems
        }
      });
    }
  };


  return (
    <div
      style={{
        position: 'fixed',
        left: isExpanded ? '0' : '-350px',
        top: '0',
        width: '350px',
        height: '100vh',
        backgroundColor: 'rgba(20, 20, 20, 0.98)',
        color: 'white',
        zIndex: isSelecting ? 9000 : 10004, // Passe derrière pendant la sélection
        transition: 'left 0.3s ease, z-index 0.1s ease',
        boxShadow: '2px 0 12px rgba(0,0,0,0.5)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        opacity: isSelecting ? 0.3 : 1 // Plus transparent pendant la sélection
      }}
    >
      {/* Bouton toggle */}
      <button
        onClick={() => {
          if (isExpanded && onClose) {
            onClose();
          } else {
            setIsExpanded(!isExpanded);
          }
        }}
        style={{
          position: 'absolute',
          right: '-45px',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '45px',
          height: '100px',
          backgroundColor: 'rgba(20, 20, 20, 0.98)',
          color: 'white',
          border: 'none',
          borderRadius: '0 8px 8px 0',
          cursor: 'pointer',
          fontSize: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '2px 0 8px rgba(0,0,0,0.4)',
          zIndex: 1
        }}
      >
        ✕
      </button>

      {/* Header */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>Configuration</h2>
          <button
            onClick={onClose}
            style={{
              padding: '4px 8px',
              backgroundColor: 'rgba(220, 53, 69, 0.8)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            ✕
          </button>
        </div>
        
        {/* Page info compacte */}
        <div style={{ 
          fontSize: '10px', 
          opacity: 0.8,
          padding: '6px 8px',
          backgroundColor: 'rgba(0, 123, 255, 0.15)',
          borderRadius: '4px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>📄 {currentPage?.pageName ?? 'Page par défaut'}</span>
          {config.PagesList.length > 1 && (
            <select
              value={currentPageUrl}
              onChange={(e) => {
                if (onNavigateToPage) {
                  onNavigateToPage(e.target.value);
                }
              }}
              style={{
                padding: '2px 6px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '3px',
                fontSize: '9px',
                cursor: 'pointer'
              }}
            >
              {config.PagesList.map((page) => (
                <option key={page.pageUrl} value={page.pageUrl} style={{ backgroundColor: '#333', color: 'white' }}>
                  {page.pageName}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.1)', overflowX: 'auto' }}>
        {[
          { id: 'layout', label: '📐 Layout', name: 'layout' },
          { id: 'design', label: '🎨 Design', name: 'design' },
          { id: 'pages', label: '📄 Pages', name: 'pages' },
          { id: 'api', label: '🔌 API', name: 'api' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.name as typeof activeTab)}
            style={{
              flex: 1,
              padding: '12px 8px',
              backgroundColor: activeTab === tab.name ? 'rgba(0, 123, 255, 0.3)' : 'transparent',
              color: 'white',
              border: 'none',
              borderBottom: activeTab === tab.name ? '2px solid #007bff' : 'none',
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: activeTab === tab.name ? 'bold' : 'normal',
              whiteSpace: 'nowrap'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
        {activeTab === 'layout' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Simulateur de breakpoints */}
            <GridSimulator
              breakpoints={currentPage?.gridLayout?.breakpoints ?? config.gridLayout.breakpoints}
              onSimulationChange={onSimulationChange || (() => {})}
              currentBreakpoint={currentPage?.gridLayout?.breakpoints?.find(b => b.name === currentBreakpoint) ?? config.gridLayout.breakpoints.find(b => b.name === currentBreakpoint)!}
            />

            {/* Info breakpoint */}
            <div style={{ 
              padding: '10px', 
              backgroundColor: 'rgba(0, 123, 255, 0.1)', 
              borderRadius: '6px',
              border: '1px solid rgba(0, 123, 255, 0.3)',
              fontSize: '12px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Breakpoint actuel : <strong>{currentBreakpoint.toUpperCase()}</strong></span>
                <span style={{ opacity: 0.7 }}>
                {(() => {
                    let itemsToShow;
                    if (currentPage?.gridLayout) {
                      itemsToShow = currentPage.gridLayout.items;
                    } else {
                      itemsToShow = config.gridLayout.items.filter(i => !i.page || i.page === currentPageUrl);
                    }
                    const inGrid = itemsToShow.filter(i => !i.outsideGrid).length;
                    const outGrid = itemsToShow.filter(i => i.outsideGrid).length;
                    return `${inGrid} dans grille, ${outGrid} hors grille`;
                })()}
                </span>
              </div>
          </div>

            {/* Position sélectionnée */}
            {selectedPosition && (
              <div style={{ 
                padding: '12px', 
                backgroundColor: 'rgba(40, 167, 69, 0.2)', 
                borderRadius: '6px',
                border: '1px solid rgba(40, 167, 69, 0.4)'
              }}>
                <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px' }}>Position sélectionnée</div>
                <div style={{ 
                  backgroundColor: 'rgba(0,0,0,0.3)', 
                  padding: '8px', 
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontFamily: 'monospace',
                  marginBottom: '8px'
                }}>
                  {currentBreakpoint}: col {selectedPosition.colStart}-{selectedPosition.colEnd}, row {selectedPosition.rowStart}-{selectedPosition.rowEnd}
                </div>
                
                {selectedComponent && (
                  <button
                    onClick={() => onApplyPosition?.(selectedComponent.id)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}
                  >
                    Appliquer à {selectedComponent.component}
                  </button>
                )}
              </div>
            )}

            {/* Composants avec pagination */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '14px' }}>Composants</h3>
                <button
                  onClick={() => setShowComponentModal(true)}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: 'bold'
                  }}
                >
                  + Ajouter Composant
                </button>
              </div>

              
              <div style={{ overflowY: 'auto' }}>
              {(() => {
                let itemsToShow;
                if (currentPage?.gridLayout) {
                  itemsToShow = currentPage.gridLayout.items;
                } else {
                  itemsToShow = config.gridLayout.items.filter(i => !i.page || i.page === currentPageUrl);
                }
                
                return itemsToShow.map((item) => {
                  const metadata = getComponentMetadata(item.component);
                  const globalParams = config.componentParams[item.component] || {};
                  const instanceParams = item.props || {};
                  // Fusionner les paramètres : global par défaut, puis instance
                  const mergedParams = { ...globalParams, ...instanceParams };
                  const isOutsideGrid = item.outsideGrid || false;
                  
                return (
                  <div 
                    key={item.id}
                    style={{ 
                      padding: '10px', 
                      backgroundColor: selectedComponent?.id === item.id ? 'rgba(0, 123, 255, 0.3)' : (isOutsideGrid ? 'rgba(255,165,0,0.1)' : 'rgba(255,255,255,0.05)'), 
                      borderRadius: '4px',
                      marginBottom: '8px',
                      fontSize: '11px',
                      border: selectedComponent?.id === item.id ? '1px solid rgba(0, 123, 255, 0.8)' : (isOutsideGrid ? '1px solid rgba(255,165,0,0.3)' : 'none')
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                              <span style={{ fontWeight: 'bold' }}>{item.component}</span>
                              {isOutsideGrid && (
                                <span style={{ 
                                  fontSize: '8px', 
                                  backgroundColor: 'rgba(255,165,0,0.3)', 
                                  padding: '2px 4px', 
                                  borderRadius: '3px',
                                  color: '#ffa500'
                                }}>
                                  HORS GRILLE
                                </span>
                              )}
                            </div>
                            <div style={{ opacity: 0.7, fontSize: '9px' }}>ID: {item.id}</div>
                            {!isOutsideGrid && item.positions?.[currentBreakpoint] && (
                              <div style={{ 
                                fontSize: '9px',
                                fontFamily: 'monospace',
                                opacity: 0.6 
                              }}>
                                Col: {item.positions[currentBreakpoint].colStart}-{item.positions[currentBreakpoint].colEnd}, 
                                Row: {item.positions[currentBreakpoint].rowStart}-{item.positions[currentBreakpoint].rowEnd}
                              </div>
                            )}
                      </div>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {!isOutsideGrid && (
                          <button
                            onClick={() => onSelectComponentForMove?.(item.id)}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: selectedComponent?.id === item.id ? '#28a745' : '#007bff',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '10px',
                              fontWeight: 'bold'
                            }}
                          >
                            {selectedComponent?.id === item.id ? '✓' : '↔'}
                          </button>
                        )}
                        <button
                          onClick={() => {
                                if (confirm(`Supprimer ${item.component} ?`)) {
                              if (currentPage?.gridLayout) {
                                const newPageItems = currentPage.gridLayout.items.filter(it => it.id !== item.id);
                                const newPages = config.PagesList.map(p => 
                                  p.pageUrl === currentPageUrl 
                                        ? { ...p, gridLayout: { ...p.gridLayout, items: newPageItems, breakpoints: p.gridLayout?.breakpoints ?? config.gridLayout.breakpoints } }
                                    : p
                                );
                                onConfigChange({ PagesList: newPages });
                              } else {
                                const newItems = config.gridLayout.items.filter(it => it.id !== item.id);
                                onConfigChange({ gridLayout: { ...config.gridLayout, items: newItems } });
                              }
                            }
                          }}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                                fontSize: '10px'
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    
                    {/* Section des paramètres du composant */}
                    {metadata && metadata.paramDefinitions && Object.keys(metadata.paramDefinitions).length > 0 && (
                      <div style={{ 
                        marginTop: '8px',
                        padding: '8px',
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        borderRadius: '4px',
                        fontSize: '10px'
                      }}>
                        <div style={{ opacity: 0.7, marginBottom: '6px', fontSize: '9px', fontWeight: 'bold' }}>
                          ⚙️ Paramètres spécifiques à cette instance :
                        </div>
                        {Object.entries(metadata.paramDefinitions).map(([key, def]) => {
                          const currentValue = mergedParams[key] ?? metadata.defaultParams[key];
                          const isOverridden = instanceParams.hasOwnProperty(key);
                          
                          return (
                            <div key={key} style={{ marginBottom: '6px' }}>
                              {def.type === 'boolean' ? (
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                  <input
                                    type="checkbox"
                                    checked={Boolean(currentValue)}
                                    onChange={(e) => handleInstanceParamChange(item.id, key, e.target.checked)}
                                    style={{ cursor: 'pointer' }}
                                  />
                                  <span style={{ fontSize: '9px', color: isOverridden ? '#28a745' : 'inherit' }}>
                                    {def.label} {isOverridden && '✓'}
                                  </span>
                                </label>
                              ) : (
                                <>
                                  <label style={{ display: 'block', marginBottom: '2px', opacity: 0.8, fontSize: '9px', color: isOverridden ? '#28a745' : 'inherit' }}>
                                    {def.label} {isOverridden && '✓'}
                                  </label>
                                  <input
                                    type={def.type === 'number' ? 'number' : 'text'}
                                    value={String(currentValue)}
                                    onChange={(e) => {
                                      const newValue = def.type === 'number' ? Number(e.target.value) : e.target.value;
                                      handleInstanceParamChange(item.id, key, newValue);
                                    }}
                                    style={{
                                      width: '90%',
                                      padding: '4px 6px',
                                      backgroundColor: 'rgba(255,255,255,0.1)',
                                      color: 'white',
                                      border: `1px solid ${isOverridden ? 'rgba(40, 167, 69, 0.5)' : 'rgba(255,255,255,0.2)'}`,
                                      borderRadius: '3px',
                                      fontSize: '9px'
                                    }}
                                  />
                                </>
                              )}
                              {def.description && (
                                <div style={{ fontSize: '8px', opacity: 0.6, marginTop: '2px' }}>
                                  {def.description}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'design' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Section Couleurs */}
                                <div>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                🎨 Couleurs
                <button
                  onClick={() => setShowAdvanced(!showAdvanced)}
                                    style={{
                    padding: '2px 6px',
                                      backgroundColor: 'rgba(255,255,255,0.1)',
                                      color: 'white',
                    border: 'none',
                                      borderRadius: '3px',
                    cursor: 'pointer',
                    fontSize: '9px',
                    marginLeft: 'auto'
                  }}
                >
                  {showAdvanced ? 'Masquer' : 'Avancé'}
                </button>
              </h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
                <div>
                  <label style={{ fontSize: '10px', display: 'block', marginBottom: '4px' }}>Primaire</label>
                  <input
                    type="color"
                    value={config.primaryColor}
                    onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                    style={{ width: '90%', height: '32px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                  />
                                </div>
                <div>
                  <label style={{ fontSize: '10px', display: 'block', marginBottom: '4px' }}>Secondaire</label>
                  <input
                    type="color"
                    value={config.secondaryColor}
                    onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                    style={{ width: '90%', height: '32px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  />
                            </div>
                <div>
                  <label style={{ fontSize: '10px', display: 'block', marginBottom: '4px' }}>Accent</label>
                  <input
                    type="color"
                    value={config.accentuatedColor}
                    onChange={(e) => handleColorChange('accentuatedColor', e.target.value)}
                    style={{ width: '90%', height: '32px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  />
                      </div>
                <div>
                  <label style={{ fontSize: '10px', display: 'block', marginBottom: '4px' }}>Mode sombre</label>
                  <input
                    type="checkbox"
                    checked={config.isDark}
                    onChange={(e) => onConfigChange({ isDark: e.target.checked })}
                    style={{ marginTop: '8px', cursor: 'pointer' }}
                  />
                  </div>
            </div>

              {showAdvanced && (
            <div style={{ 
                  padding: '12px', 
                  backgroundColor: 'rgba(255,255,255,0.05)', 
                  borderRadius: '6px',
                  marginBottom: '16px'
                }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '12px' }}>Couleurs Mode Sombre</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <div>
                      <label style={{ fontSize: '9px', display: 'block', marginBottom: '2px' }}>Primaire sombre</label>
                      <input
                        type="color"
                        value={config.darkPrimaryColor}
                        onChange={(e) => handleColorChange('darkPrimaryColor', e.target.value)}
                        style={{ width: '90%', height: '28px', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '9px', display: 'block', marginBottom: '2px' }}>Secondaire sombre</label>
                      <input
                        type="color"
                        value={config.darkSecondaryColor}
                        onChange={(e) => handleColorChange('darkSecondaryColor', e.target.value)}
                        style={{ width: '90%', height: '28px', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '9px', display: 'block', marginBottom: '2px' }}>Accent sombre</label>
                      <input
                        type="color"
                        value={config.darkAccentuatedColor}
                        onChange={(e) => handleColorChange('darkAccentuatedColor', e.target.value)}
                        style={{ width: '90%', height: '28px', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                      />
                    </div>
                  </div>
                  </div>
              )}
            </div>

            {/* Section Paramètres Avancés du Site */}
            {showAdvanced && (
              <div>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '14px' }}>🔧 Paramètres Avancés</h3>
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: 'rgba(255,255,255,0.05)', 
                  borderRadius: '6px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  marginBottom: '16px'
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '11px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '10px', opacity: 0.8, marginBottom: '4px' }}>
                        Nom du site
                      </label>
                      <input
                        type="text"
                        value={config.SiteName}
                        onChange={(e) => onConfigChange({ SiteName: e.target.value })}
                        style={{
                          width: '90%',
                          padding: '6px 8px',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          color: 'white',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '4px',
                          fontSize: '11px'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '10px', opacity: 0.8, marginBottom: '4px' }}>
                        Police de caractères
                      </label>
                      <input
                        type="text"
                        value={config.fontFamily}
                        onChange={(e) => onConfigChange({ fontFamily: e.target.value })}
                        style={{
                          width: '90%',
                          padding: '6px 8px',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          color: 'white',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '4px',
                          fontSize: '11px'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '10px', opacity: 0.8, marginBottom: '4px' }}>
                        Chemin des assets
                      </label>
                      <input
                        type="text"
                        value={config.assetsBasePath}
                        onChange={(e) => onConfigChange({ assetsBasePath: e.target.value })}
                        style={{
                          width: '90%',
                          padding: '6px 8px',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          color: 'white',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '4px',
                          fontSize: '11px'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '10px', opacity: 0.8, marginBottom: '4px' }}>
                        Token du site
                      </label>
                      <input
                        type="text"
                        value={config.websiteToken}
                        onChange={(e) => onConfigChange({ websiteToken: e.target.value })}
                        style={{
                          width: '90%',
                          padding: '6px 8px',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          color: 'white',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '4px',
                          fontSize: '11px'
                        }}
                      />
                    </div>
                  </div>
                  
                  <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={config.darkModeEnabled}
                        onChange={(e) => onConfigChange({ darkModeEnabled: e.target.checked })}
                      />
                      <span>Mode sombre activé</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Section Modules */}
            <div>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '14px' }}>⚙️ Modules</h3>
              <div>
                {Object.entries(config.modules).map(([moduleName, isEnabled]) => {
                  const metadata = getComponentMetadata(moduleName);
                  return (
                    <div key={moduleName} style={{ 
                      padding: '12px', 
                      backgroundColor: 'rgba(255,255,255,0.05)', 
                      borderRadius: '6px',
                      marginBottom: '8px',
                      border: '1px solid rgba(255,255,255,0.1)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{moduleName}</span>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px' }}>
                          <input
                            type="checkbox"
                            checked={isEnabled}
                            onChange={(e) => {
                              const newModules = { ...config.modules, [moduleName]: e.target.checked };
                              onConfigChange({ modules: newModules });
                            }}
                            style={{ cursor: 'pointer' }}
                          />
                          Activé
                        </label>
                      </div>
                      
                      {/* Paramètres du module */}
                      {metadata && metadata.paramDefinitions && Object.keys(metadata.paramDefinitions).length > 0 && (
                        <div style={{ 
                          marginTop: '8px',
                          padding: '8px',
                          backgroundColor: 'rgba(0,0,0,0.2)',
                          borderRadius: '4px',
                          fontSize: '10px'
                        }}>
                          <div style={{ opacity: 0.7, marginBottom: '6px', fontSize: '9px' }}>Paramètres :</div>
                          {Object.entries(metadata.paramDefinitions).map(([key, def]) => {
                            const currentValue = config.componentParams[moduleName]?.[key] ?? metadata.defaultParams[key];
                            
                            return (
                              <div key={key} style={{ marginBottom: '6px' }}>
                                {def.type === 'boolean' ? (
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                    <input
                                      type="checkbox"
                                      checked={Boolean(currentValue)}
                                      onChange={(e) => handleModuleParamChange(moduleName, key, e.target.checked)}
                                      style={{ cursor: 'pointer' }}
                                    />
                                    <span>{def.label}</span>
                                  </label>
                                ) : (
                                  <>
                                    <label style={{ display: 'block', marginBottom: '2px', opacity: 0.8, fontSize: '9px' }}>
                                      {def.label}
                                    </label>
                                    <input
                                      type={def.type === 'number' ? 'number' : 'text'}
                                      value={String(currentValue)}
                                      onChange={(e) => {
                                        const newValue = def.type === 'number' ? Number(e.target.value) : e.target.value;
                                        handleModuleParamChange(moduleName, key, newValue);
                                      }}
                                      style={{
                                        width: '90%',
                                        padding: '4px 6px',
                                        backgroundColor: 'rgba(255,255,255,0.1)',
                                        color: 'white',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        borderRadius: '3px',
                                        fontSize: '10px'
                                      }}
                                    />
                                  </>
                                )}
                                {def.description && (
                                  <div style={{ fontSize: '8px', opacity: 0.6, marginTop: '2px' }}>
                                    {def.description}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}



        {activeTab === 'pages' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Actions rapides */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => {
                  const newPage = {
                    pageName: `Page ${config.PagesList.length + 1}`,
                    pageUrl: `/page-${Date.now()}`,
                    InNavBar: true,
                    description: 'Nouvelle page',
                    gridLayout: {
                      ...config.gridLayout,
                      items: []
                    },
                    pageSpecificModules: {},
                    pageSpecificParams: {}
                  };
                  onConfigChange({ PagesList: [...config.PagesList, newPage] });
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}
              >
                ➕ Nouvelle Page
              </button>
            </div>

            {/* Liste des pages */}
            <div>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '14px' }}>
                Pages du site ({config.PagesList.length})
              </h3>
              <div style={{overflowY: 'auto' }}>
            {config.PagesList.map((page, index) => (
              <div 
                key={index}
                style={{ 
                  padding: '12px', 
                  backgroundColor: page.pageUrl === currentPageUrl ? 'rgba(0, 123, 255, 0.2)' : 'rgba(255,255,255,0.05)', 
                  borderRadius: '6px',
                      border: page.pageUrl === currentPageUrl ? '2px solid rgba(0, 123, 255, 0.8)' : '1px solid rgba(255,255,255,0.1)',
                      marginBottom: '8px'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      fontWeight: 'bold', 
                      fontSize: '12px', 
                      marginBottom: '4px',
                      color: page.pageUrl === currentPageUrl ? '#00ff00' : 'inherit'
                    }}>
                      {page.pageUrl === currentPageUrl && '▶ '}{page.pageName}
                    </div>
                        <div style={{ fontSize: '10px', opacity: 0.7, fontFamily: 'monospace', marginBottom: '4px' }}>
                      {page.pageUrl}
                    </div>
                        <div style={{ fontSize: '9px', opacity: 0.5 }}>
                      {page.gridLayout?.items?.length ?? config.gridLayout.items.filter(i => !i.page || i.page === page.pageUrl).length} composants
                          {page.gridLayout ? ' • Grille personnalisée' : ' • Grille globale'}
                    </div>
                  </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button
                        onClick={() => {
                          if (onNavigateToPage) {
                            onNavigateToPage(page.pageUrl);
                          }
                        }}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: page.pageUrl === currentPageUrl ? '#28a745' : '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '10px'
                        }}
                      >
                        {page.pageUrl === currentPageUrl ? '✓' : '👁️'}
                      </button>
                      <button
                        onClick={() => {
                            if (confirm(`Supprimer "${page.pageName}" ?`)) {
                            const newPages = config.PagesList.filter((_, i) => i !== index);
                            onConfigChange({ PagesList: newPages });
                          }
                        }}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '10px'
                        }}
                      >
                        🗑️
                      </button>
                  </div>
                </div>

                    {/* Configuration rapide */}
                    <div style={{ 
                      padding: '8px',
                      backgroundColor: 'rgba(0,0,0,0.2)',
                      borderRadius: '4px',
                      fontSize: '10px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={page.InNavBar}
                      onChange={(e) => {
                        const newPages = [...config.PagesList];
                        newPages[index] = { ...page, InNavBar: e.target.checked };
                        onConfigChange({ PagesList: newPages });
                      }}
                    />
                          <span>Dans la navbar</span>
                  </label>
                        {page.gridLayout ? (
                          <span style={{ color: '#28a745', fontSize: '9px' }}>✓ Grille personnalisée</span>
                        ) : (
                          <span style={{ color: '#ffc107', fontSize: '9px' }}>⚠ Grille globale</span>
                        )}
                      </div>
                      
                      <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
                        <input
                          type="text"
                          value={page.pageName}
                          onChange={(e) => {
                            const newPages = [...config.PagesList];
                            newPages[index] = { ...page, pageName: e.target.value };
                            onConfigChange({ PagesList: newPages });
                          }}
                          style={{
                            flex: 1,
                            padding: '4px 6px',
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            color: 'white',
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: '3px',
                            fontSize: '10px'
                          }}
                          placeholder="Nom de la page"
                        />
                        <button
                          onClick={() => {
                            const newPages = [...config.PagesList];
                            newPages[index] = { 
                              ...page, 
                              gridLayout: page.gridLayout ? undefined : {
                                ...config.gridLayout,
                                items: []
                              }
                            };
                            onConfigChange({ PagesList: newPages });
                          }}
                          style={{
                            padding: '4px 6px',
                            backgroundColor: page.gridLayout ? '#dc3545' : '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            fontSize: '9px'
                          }}
                        >
                          {page.gridLayout ? 'Supprimer grille' : 'Créer grille'}
                        </button>
                      </div>

                      {/* Paramètres de grille si page a une grille personnalisée */}
                      {page.gridLayout && (
                        <div style={{ 
                          marginTop: '6px',
                          padding: '6px',
                          backgroundColor: 'rgba(0, 123, 255, 0.1)',
                          borderRadius: '3px',
                          border: '1px solid rgba(0, 123, 255, 0.3)',
                          fontSize: '9px'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                            <div style={{ fontWeight: 'bold', color: '#007bff' }}>
                              ⚙️ Paramètres de Grille
                            </div>
                            <button
                              onClick={() => {
                                setShowAdvancedGridParams(prev => ({
                                  ...prev,
                                  [page.pageUrl]: !prev[page.pageUrl]
                                }));
                              }}
                              style={{
                                padding: '2px 6px',
                                backgroundColor: showAdvancedGridParams[page.pageUrl] ? '#dc3545' : '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '2px',
                                cursor: 'pointer',
                                fontSize: '7px',
                                fontWeight: 'bold'
                              }}
                            >
                              {showAdvancedGridParams[page.pageUrl] ? 'Réduire' : 'Tout voir'}
                            </button>
                          </div>
                          
                          {/* Paramètres de base toujours visibles */}
                          <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', fontSize: '8px', opacity: 0.7, marginBottom: '1px' }}>
                                Gap horizontal
                              </label>
                              <input
                                type="number"
                                value={page.gridLayout.gap ?? 0}
                                onChange={(e) => {
                                  const newPages = [...config.PagesList];
                                  newPages[index] = { 
                                    ...page, 
                                    gridLayout: { 
                                      ...page.gridLayout, 
                                      gap: Number(e.target.value),
                                      breakpoints: page.gridLayout?.breakpoints ?? config.gridLayout.breakpoints,
                                      items: page.gridLayout?.items ?? []
                                    }
                                  };
                                  onConfigChange({ PagesList: newPages });
                                }}
                                style={{
                                  width: '90%',
                                  padding: '2px 4px',
                                  backgroundColor: 'rgba(255,255,255,0.1)',
                                  color: 'white',
                                  border: '1px solid rgba(255,255,255,0.2)',
                                  borderRadius: '2px',
                                  fontSize: '8px'
                                }}
                              />
                            </div>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', fontSize: '8px', opacity: 0.7, marginBottom: '1px' }}>
                                Gap vertical
                              </label>
                              <input
                                type="number"
                                value={page.gridLayout.rowGap ?? page.gridLayout.gap ?? 0}
                                onChange={(e) => {
                                  const newPages = [...config.PagesList];
                                  newPages[index] = { 
                                    ...page, 
                                    gridLayout: { 
                                      ...page.gridLayout, 
                                      rowGap: Number(e.target.value),
                                      breakpoints: page.gridLayout?.breakpoints ?? config.gridLayout.breakpoints,
                                      items: page.gridLayout?.items ?? []
                                    }
                                  };
                                  onConfigChange({ PagesList: newPages });
                                }}
                                style={{
                                  width: '90%',
                                  padding: '2px 4px',
                                  backgroundColor: 'rgba(255,255,255,0.1)',
                                  color: 'white',
                                  border: '1px solid rgba(255,255,255,0.2)',
                                  borderRadius: '2px',
                                  fontSize: '8px'
                                }}
                              />
                            </div>
                          </div>

                          {/* Paramètres avancés (déroulables) */}
                          {showAdvancedGridParams[page.pageUrl] && (
                            <div style={{ 
                              marginTop: '6px',
                              padding: '6px',
                              backgroundColor: 'rgba(0,0,0,0.2)',
                              borderRadius: '3px',
                              border: '1px solid rgba(255,255,255,0.1)'
                            }}>
                              {/* Overlay */}
                              <div style={{ marginBottom: '8px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '8px', marginBottom: '4px' }}>
                                  <input
                                    type="checkbox"
                                    checked={page.gridLayout.overlay?.enabled ?? false}
                                    onChange={(e) => {
                                      const newPages = [...config.PagesList];
                                      newPages[index] = { 
                                        ...page, 
                                        gridLayout: { 
                                          ...page.gridLayout, 
                                          overlay: {
                                            ...page.gridLayout?.overlay,
                                            enabled: e.target.checked
                                          },
                                          breakpoints: page.gridLayout?.breakpoints ?? config.gridLayout.breakpoints,
                                          items: page.gridLayout?.items ?? []
                                        }
                                      };
                                      onConfigChange({ PagesList: newPages });
                                    }}
                                  />
                                  <span>Overlay activé</span>
                                </label>
                                
                                {page.gridLayout.overlay?.enabled && (
                                  <div style={{ marginLeft: '16px', fontSize: '7px' }}>
                                    <div style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '2px', cursor: 'pointer' }}>
                                        <input
                                          type="checkbox"
                                          checked={page.gridLayout.overlay?.showColumns ?? false}
                                          onChange={(e) => {
                                            const newPages = [...config.PagesList];
                                            newPages[index] = { 
                                              ...page, 
                                              gridLayout: { 
                                                ...page.gridLayout, 
                                                overlay: {
                                                  ...page.gridLayout?.overlay,
                                                  enabled: page.gridLayout?.overlay?.enabled ?? false,
                                                  showColumns: e.target.checked
                                                },
                                                breakpoints: page.gridLayout?.breakpoints ?? config.gridLayout.breakpoints,
                                                items: page.gridLayout?.items ?? []
                                              }
                                            };
                                            onConfigChange({ PagesList: newPages });
                                          }}
                                        />
                                        <span>Colonnes</span>
                                      </label>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '2px', cursor: 'pointer' }}>
                                        <input
                                          type="checkbox"
                                          checked={page.gridLayout.overlay?.showRows ?? false}
                                          onChange={(e) => {
                                            const newPages = [...config.PagesList];
                                            newPages[index] = { 
                                              ...page, 
                                              gridLayout: { 
                                                ...page.gridLayout, 
                                                overlay: {
                                                  ...page.gridLayout?.overlay,
                                                  enabled: page.gridLayout?.overlay?.enabled ?? false,
                                                  showRows: e.target.checked
                                                },
                                                breakpoints: page.gridLayout?.breakpoints ?? config.gridLayout.breakpoints,
                                                items: page.gridLayout?.items ?? []
                                              }
                                            };
                                            onConfigChange({ PagesList: newPages });
                                          }}
                                        />
                                        <span>Lignes</span>
                                      </label>
                                    </div>
                                    <div style={{ display: 'flex', gap: '4px' }}>
                                      <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', fontSize: '7px', opacity: 0.7, marginBottom: '1px' }}>Couleur</label>
                                        <input
                                          type="color"
                                          value={page.gridLayout.overlay?.color ?? '#ff0000'}
                                          onChange={(e) => {
                                            const newPages = [...config.PagesList];
                                            newPages[index] = { 
                                              ...page, 
                                              gridLayout: { 
                                                ...page.gridLayout, 
                                                overlay: {
                                                  ...page.gridLayout?.overlay,
                                                  enabled: page.gridLayout?.overlay?.enabled ?? false,
                                                  color: e.target.value
                                                },
                                                breakpoints: page.gridLayout?.breakpoints ?? config.gridLayout.breakpoints,
                                                items: page.gridLayout?.items ?? []
                                              }
                                            };
                                            onConfigChange({ PagesList: newPages });
                                          }}
                                          style={{
                                            width: '90%',
                                            height: '16px',
                                            border: 'none',
                                            borderRadius: '2px',
                                            cursor: 'pointer'
                                          }}
                                        />
                                      </div>
                                      <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', fontSize: '7px', opacity: 0.7, marginBottom: '1px' }}>Opacité</label>
                                        <input
                                          type="range"
                                          min="0"
                                          max="1"
                                          step="0.1"
                                          value={page.gridLayout.overlay?.opacity ?? 0.5}
                                          onChange={(e) => {
                                            const newPages = [...config.PagesList];
                                            newPages[index] = { 
                                              ...page, 
                                              gridLayout: { 
                                                ...page.gridLayout, 
                                                overlay: {
                                                  ...page.gridLayout?.overlay,
                                                  enabled: page.gridLayout?.overlay?.enabled ?? false,
                                                  opacity: Number(e.target.value)
                                                },
                                                breakpoints: page.gridLayout?.breakpoints ?? config.gridLayout.breakpoints,
                                                items: page.gridLayout?.items ?? []
                                              }
                                            };
                                            onConfigChange({ PagesList: newPages });
                                          }}
                                          style={{
                                            width: '90%',
                                            height: '16px'
                                          }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Breakpoints */}
                              <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                  <div style={{ fontSize: '8px', opacity: 0.8, fontWeight: 'bold' }}>
                                    Breakpoints ({page.gridLayout.breakpoints?.length ?? config.gridLayout.breakpoints.length})
                                  </div>
                                  <button
                                    onClick={() => {
                                      const newPages = [...config.PagesList];
                                      const newBreakpoints = [...(page.gridLayout?.breakpoints ?? config.gridLayout.breakpoints)];
                                      const newBpName = String.fromCharCode(97 + newBreakpoints.length);
                                      newBreakpoints.push({
                                        name: newBpName as any,
                                        minWidth: newBreakpoints.length * 320,
                                        columns: Math.min(newBreakpoints.length + 1, 6)
                                      });
                                      newPages[index] = { 
                                        ...page, 
                                        gridLayout: { 
                                          ...page.gridLayout, 
                                          breakpoints: newBreakpoints,
                                          items: page.gridLayout?.items ?? []
                                        }
                                      };
                                      onConfigChange({ PagesList: newPages });
                                    }}
                                    style={{
                                      padding: '1px 4px',
                                      backgroundColor: '#28a745',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '2px',
                                      cursor: 'pointer',
                                      fontSize: '6px'
                                    }}
                                  >
                                    + Ajouter
                                  </button>
                                </div>
                                
                                {(page.gridLayout.breakpoints ?? config.gridLayout.breakpoints).map((breakpoint, bpIndex) => (
                                  <div key={bpIndex} style={{ 
                                    display: 'flex', 
                                    gap: '2px', 
                                    marginBottom: '3px',
                                    alignItems: 'center',
                                    padding: '2px',
                                    backgroundColor: 'rgba(255,255,255,0.05)',
                                    borderRadius: '2px'
                                  }}>
                                    <span style={{ fontSize: '7px', minWidth: '12px', opacity: 0.7 }}>
                                      {breakpoint.name}:
                                    </span>
                                    <input
                                      type="number"
                                      placeholder="minWidth"
                                      value={breakpoint.minWidth}
                                      onChange={(e) => {
                                        const newPages = [...config.PagesList];
                                        const newBreakpoints = [...(page.gridLayout?.breakpoints ?? config.gridLayout.breakpoints)];
                                        newBreakpoints[bpIndex] = {
                                          ...breakpoint,
                                          minWidth: Number(e.target.value)
                                        };
                                        newPages[index] = { 
                                          ...page, 
                                          gridLayout: { 
                                            ...page.gridLayout, 
                                            breakpoints: newBreakpoints,
                                            items: page.gridLayout?.items ?? []
                                          }
                                        };
                                        onConfigChange({ PagesList: newPages });
                                      }}
                                      style={{
                                        flex: 1,
                                        padding: '2px 3px',
                                        backgroundColor: 'rgba(255,255,255,0.1)',
                                        color: 'white',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        borderRadius: '2px',
                                        fontSize: '7px'
                                      }}
                                    />
                                    <input
                                      type="number"
                                      placeholder="cols"
                                      value={breakpoint.columns}
                                      onChange={(e) => {
                                        const newPages = [...config.PagesList];
                                        const newBreakpoints = [...(page.gridLayout?.breakpoints ?? config.gridLayout.breakpoints)];
                                        newBreakpoints[bpIndex] = {
                                          ...breakpoint,
                                          columns: Number(e.target.value)
                                        };
                                        newPages[index] = { 
                                          ...page, 
                                          gridLayout: { 
                                            ...page.gridLayout, 
                                            breakpoints: newBreakpoints,
                                            items: page.gridLayout?.items ?? []
                                          }
                                        };
                                        onConfigChange({ PagesList: newPages });
                                      }}
                                      style={{
                                        flex: 1,
                                        padding: '2px 3px',
                                        backgroundColor: 'rgba(255,255,255,0.1)',
                                        color: 'white',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        borderRadius: '2px',
                                        fontSize: '7px'
                                      }}
                                    />
                                    {(page.gridLayout?.breakpoints ?? config.gridLayout.breakpoints).length > 1 && (
                                      <button
                                        onClick={() => {
                                          if (confirm(`Supprimer le breakpoint ${breakpoint.name} ?`)) {
                                            const newPages = [...config.PagesList];
                                            const newBreakpoints = [...(page.gridLayout?.breakpoints ?? config.gridLayout.breakpoints)];
                                            newBreakpoints.splice(bpIndex, 1);
                                            newPages[index] = { 
                                              ...page, 
                                              gridLayout: { 
                                                ...page.gridLayout, 
                                                breakpoints: newBreakpoints,
                                                items: page.gridLayout?.items ?? []
                                              }
                                            };
                                            onConfigChange({ PagesList: newPages });
                                          }
                                        }}
                                        style={{
                                          padding: '2px 3px',
                                          backgroundColor: '#dc3545',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '2px',
                                          cursor: 'pointer',
                                          fontSize: '6px'
                                        }}
                                      >
                                        ×
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'api' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Configuration de l'API */}
            <div>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '14px' }}>⚙️ Configuration de l'API</h3>
              <div style={{ 
                padding: '12px', 
                backgroundColor: 'rgba(255,255,255,0.05)', 
                borderRadius: '6px',
                border: '1px solid rgba(255,255,255,0.1)',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'grid', gap: '12px', fontSize: '11px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '10px', opacity: 0.8, marginBottom: '4px' }}>
                      URL de base de l'API
                    </label>
                    <input
                      type="text"
                      value={config.api.baseUrl}
                      onChange={(e) => onConfigChange({ api: { ...config.api, baseUrl: e.target.value } })}
                      placeholder="http://localhost:8000"
                      style={{
                        width: '90%',
                        padding: '6px 8px',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '4px',
                        fontSize: '11px'
                      }}
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', fontSize: '10px', opacity: 0.8, marginBottom: '4px' }}>
                      Token de lecture (GET)
                    </label>
                    <input
                      type="password"
                      value={config.api.readToken}
                      onChange={(e) => onConfigChange({ api: { ...config.api, readToken: e.target.value } })}
                      placeholder="Token pour les opérations de lecture"
                      style={{
                        width: '90%',
                        padding: '6px 8px',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '4px',
                        fontSize: '11px'
                      }}
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', fontSize: '10px', opacity: 0.8, marginBottom: '4px' }}>
                      Token d'écriture (POST, PUT, DELETE)
                    </label>
                    <input
                      type="password"
                      value={config.api.writeToken}
                      onChange={(e) => onConfigChange({ api: { ...config.api, writeToken: e.target.value } })}
                      placeholder="Token pour les opérations d'écriture"
                      style={{
                        width: '90%',
                        padding: '6px 8px',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '4px',
                        fontSize: '11px'
                      }}
                    />
                  </div>
                  
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={config.api.enableSync}
                        onChange={(e) => onConfigChange({ api: { ...config.api, enableSync: e.target.checked } })}
                      />
                      <span>Activer la synchronisation</span>
                    </label>
                    
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={config.api.autoLoadParams}
                        onChange={(e) => onConfigChange({ api: { ...config.api, autoLoadParams: e.target.checked } })}
                      />
                      <span>Charger paramètres auto</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Synchronisation des modules */}
            {config.api.enableSync && (
              <div>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '14px' }}>🔄 Synchronisation des Modules</h3>
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: 'rgba(255,255,255,0.05)', 
                  borderRadius: '6px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  marginBottom: '16px'
                }}>
                  <div style={{ fontSize: '10px', marginBottom: '12px', opacity: 0.8 }}>
                    Compare et synchronise les modules locaux avec l'API. Cette opération va :
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      <li>Créer les modules manquants dans l'API</li>
                      <li>Mettre à jour les modules modifiés</li>
                      <li>Supprimer les modules non utilisés</li>
                    </ul>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                    <button
                      onClick={async () => {
                        setSyncInProgress(true);
                        setSyncResult(null);
                        try {
                          const result = await compareModules();
                          if (result) {
                            const message = `
📊 Résultat de la comparaison :
✨ À créer : ${result.modulesToCreate.length} (${result.modulesToCreate.join(', ') || 'aucun'})
🔄 À mettre à jour : ${result.modulesToUpdate.length} (${result.modulesToUpdate.map(m => m.name).join(', ') || 'aucun'})
🗑️ À supprimer : ${result.modulesToDelete.length} (${result.modulesToDelete.join(', ') || 'aucun'})
${result.errors.length > 0 ? `\n❌ Erreurs : ${result.errors.join(', ')}` : ''}
                            `.trim();
                            setSyncResult(message);
                          }
                        } catch (err) {
                          setSyncResult(`❌ Erreur : ${err instanceof Error ? err.message : String(err)}`);
                        } finally {
                          setSyncInProgress(false);
                        }
                      }}
                      disabled={syncInProgress || isLoading}
                      style={{
                        flex: 1,
                        padding: '10px',
                        backgroundColor: syncInProgress ? '#6c757d' : '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: syncInProgress ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        opacity: syncInProgress ? 0.6 : 1
                      }}
                    >
                      {syncInProgress ? '⏳ Comparaison...' : '🔍 Comparer avec l\'API'}
                    </button>

                    <button
                      onClick={async () => {
                        if (!confirm('⚠️ Synchroniser les modules avec l\'API ? Cette action va modifier l\'API.')) {
                          return;
                        }
                        
                        setSyncInProgress(true);
                        setSyncResult(null);
                        try {
                          // Récupérer la liste des modules utilisés
                          const usedModules = [
                            ...Object.keys(config.modules),
                            ...config.gridLayout.items.map(item => item.component),
                            ...config.PagesList.flatMap(page => 
                              page.gridLayout?.items.map(item => item.component) || []
                            )
                          ];
                          const uniqueUsedModules = Array.from(new Set(usedModules));
                          
                          const result = await syncModules(uniqueUsedModules);
                          if (result) {
                            const message = `
✅ Synchronisation terminée !
✨ Créés : ${result.created.length} (${result.created.join(', ') || 'aucun'})
🔄 Mis à jour : ${result.updated.length} (${result.updated.join(', ') || 'aucun'})
🗑️ Supprimés : ${result.deleted.length} (${result.deleted.join(', ') || 'aucun'})
${result.errors.length > 0 ? `\n❌ Erreurs : ${result.errors.join('\n')}` : ''}
                            `.trim();
                            setSyncResult(message);
                          }
                        } catch (err) {
                          setSyncResult(`❌ Erreur : ${err instanceof Error ? err.message : String(err)}`);
                        } finally {
                          setSyncInProgress(false);
                        }
                      }}
                      disabled={syncInProgress || isLoading}
                      style={{
                        flex: 1,
                        padding: '10px',
                        backgroundColor: syncInProgress ? '#6c757d' : '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: syncInProgress ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        opacity: syncInProgress ? 0.6 : 1
                      }}
                    >
                      {syncInProgress ? '⏳ Synchronisation...' : '🔄 Synchroniser'}
                    </button>
                  </div>

                  {syncResult && (
                    <div style={{
                      padding: '10px',
                      backgroundColor: syncResult.includes('❌') ? 'rgba(220, 53, 69, 0.2)' : 'rgba(40, 167, 69, 0.2)',
                      borderRadius: '4px',
                      border: `1px solid ${syncResult.includes('❌') ? 'rgba(220, 53, 69, 0.4)' : 'rgba(40, 167, 69, 0.4)'}`,
                      fontSize: '10px',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}>
                      {syncResult}
                    </div>
                  )}
                  
                  {apiError && (
                    <div style={{
                      padding: '10px',
                      backgroundColor: 'rgba(220, 53, 69, 0.2)',
                      borderRadius: '4px',
                      border: '1px solid rgba(220, 53, 69, 0.4)',
                      fontSize: '10px',
                      color: '#ff6b6b',
                      marginTop: '8px'
                    }}>
                      ❌ Erreur API : {apiError}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Synchronisation des instances de modules */}
            {config.api.enableSync && (
              <div>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '14px' }}>🎯 Synchronisation des Instances</h3>
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: 'rgba(255,255,255,0.05)', 
                  borderRadius: '6px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  marginBottom: '16px'
                }}>
                  <div style={{ fontSize: '10px', marginBottom: '12px', opacity: 0.8 }}>
                    Synchronise les <strong>instances</strong> de modules (chaque utilisation d'un module avec ses données spécifiques).
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      <li>Créer les instances manquantes dans l'API</li>
                      <li>Mettre à jour les données modifiées</li>
                      <li>Supprimer les instances non utilisées</li>
                    </ul>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                    <button
                      onClick={async () => {
                        setSyncInProgress(true);
                        setSyncResult(null);
                        try {
                          // 🆕 Récupérer les items de TOUTES les pages
                          const allItems: LayoutItem[] = [];
                          
                          // Items de la grille globale
                          allItems.push(...config.gridLayout.items);
                          
                          // Items des grilles spécifiques des pages
                          config.PagesList.forEach(page => {
                            if (page.gridLayout && page.gridLayout.items) {
                              const pageItems = page.gridLayout.items.map(item => ({
                                ...item,
                                page: page.pageUrl // S'assurer que la page est définie
                              }));
                              allItems.push(...pageItems);
                            }
                          });
                          
                          console.log(`🔍 Comparaison de ${allItems.length} items de toutes les pages`);
                          
                          // 🆕 Récupérer les pages depuis l'éditeur de config (localStorage)
                          let allPages = config.PagesList;
                          try {
                            const savedConfig = localStorage.getItem('gandc_config_backup');
                            if (savedConfig) {
                              const parsedConfig = JSON.parse(savedConfig);
                              if (parsedConfig.PagesList && Array.isArray(parsedConfig.PagesList)) {
                                allPages = parsedConfig.PagesList;
                                console.log(`📄 Pages trouvées dans l'éditeur: ${allPages.length}`);
                              }
                            }
                          } catch (error) {
                            console.warn('⚠️ Erreur lors de la récupération des pages depuis localStorage:', error);
                          }
                          
                          console.log(`🔍 Comparaison de ${allItems.length} items de toutes les pages`);
                          console.log(`📋 Items trouvés:`, allItems.map(i => ({ id: i.id, component: i.component, page: i.page })));
                          console.log(`📄 Pages disponibles:`, allPages.map(p => ({ name: p.pageName, url: p.pageUrl })));
                          const itemsToSync = allItems;
                          
                          const result = await compareInstances(itemsToSync, undefined); // undefined = toutes les pages
                          if (result) {
                            const message = `
📊 Instances - Résultat de la comparaison :
✨ À créer : ${result.created.length} (${result.created.join(', ') || 'aucune'})
🔄 À mettre à jour : ${result.updated.length} (${result.updated.join(', ') || 'aucune'})
🗑️ À supprimer : ${result.deleted.length} (${result.deleted.join(', ') || 'aucune'})
${result.errors.length > 0 ? `\n❌ Erreurs : ${result.errors.join(', ')}` : ''}
                            `.trim();
                            setSyncResult(message);
                          }
                        } catch (err) {
                          setSyncResult(`❌ Erreur : ${err instanceof Error ? err.message : String(err)}`);
                        } finally {
                          setSyncInProgress(false);
                        }
                      }}
                      disabled={syncInProgress || isLoading}
                      style={{
                        flex: 1,
                        padding: '10px',
                        backgroundColor: syncInProgress ? '#6c757d' : '#17a2b8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: syncInProgress ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        opacity: syncInProgress ? 0.6 : 1
                      }}
                    >
                      {syncInProgress ? '⏳ Comparaison...' : '🔍 Comparer Instances'}
                    </button>

                    <button
                      onClick={async () => {
                        if (!confirm('⚠️ Synchroniser les instances avec l\'API ? Cette action va modifier l\'API.')) {
                          return;
                        }
                        
                        setSyncInProgress(true);
                        setSyncResult(null);
                        try {
                          // 🆕 Récupérer les items de TOUTES les pages
                          const allItems: LayoutItem[] = [];
                          
                          // Items de la grille globale
                          allItems.push(...config.gridLayout.items);
                          
                          // Items des grilles spécifiques des pages
                          config.PagesList.forEach(page => {
                            if (page.gridLayout && page.gridLayout.items) {
                              const pageItems = page.gridLayout.items.map(item => ({
                                ...item,
                                page: page.pageUrl // S'assurer que la page est définie
                              }));
                              allItems.push(...pageItems);
                            }
                          });
                          
                          console.log(`🎯 Synchronisation de ${allItems.length} items de toutes les pages`);
                          
                          // 🆕 Récupérer les pages depuis l'éditeur de config (localStorage)
                          let allPages = config.PagesList;
                          try {
                            const savedConfig = localStorage.getItem('gandc_config_backup');
                            if (savedConfig) {
                              const parsedConfig = JSON.parse(savedConfig);
                              if (parsedConfig.PagesList && Array.isArray(parsedConfig.PagesList)) {
                                allPages = parsedConfig.PagesList;
                                console.log(`📄 Pages trouvées dans l'éditeur: ${allPages.length}`);
                              }
                            }
                          } catch (error) {
                            console.warn('⚠️ Erreur lors de la récupération des pages depuis localStorage:', error);
                          }
                          
                          console.log(`🔄 Synchronisation de ${allItems.length} items de toutes les pages`);
                          console.log(`📋 Items trouvés:`, allItems.map(i => ({ id: i.id, component: i.component, page: i.page })));
                          console.log(`📄 Pages disponibles:`, allPages.map(p => ({ name: p.pageName, url: p.pageUrl })));
                          const itemsToSync = allItems;
                          
                          const result = await syncInstances(itemsToSync, undefined); // undefined = toutes les pages
                          if (result) {
                            const message = `
✅ Instances - Synchronisation terminée !
✨ Créées : ${result.created.length} (${result.created.join(', ') || 'aucune'})
🔄 Mises à jour : ${result.updated.length} (${result.updated.join(', ') || 'aucune'})
🗑️ Supprimées : ${result.deleted.length} (${result.deleted.join(', ') || 'aucune'})
${result.errors.length > 0 ? `\n❌ Erreurs : ${result.errors.join('\n')}` : ''}
                            `.trim();
                            setSyncResult(message);
                          }
                        } catch (err) {
                          setSyncResult(`❌ Erreur : ${err instanceof Error ? err.message : String(err)}`);
                        } finally {
                          setSyncInProgress(false);
                        }
                      }}
                      disabled={syncInProgress || isLoading}
                      style={{
                        flex: 1,
                        padding: '10px',
                        backgroundColor: syncInProgress ? '#6c757d' : '#ffc107',
                        color: '#000',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: syncInProgress ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        opacity: syncInProgress ? 0.6 : 1
                      }}
                    >
                      {syncInProgress ? '⏳ Synchronisation...' : '🎯 Synchroniser Instances'}
                    </button>
                  </div>

                  <div style={{ 
                    padding: '8px', 
                    backgroundColor: 'rgba(255, 193, 7, 0.1)', 
                    borderRadius: '4px',
                    fontSize: '9px',
                    opacity: 0.8,
                    marginBottom: '8px'
                  }}>
                    💡 <strong>Page actuelle :</strong> {currentPage?.pageName || 'Toutes les pages'} - Seules les instances de cette page seront synchronisées
                  </div>

                  {/* Bouton pour publier toutes les instances */}
                  <div style={{ marginTop: '12px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '12px' }}>
                    <div style={{ fontSize: '9px', marginBottom: '8px', opacity: 0.8 }}>
                      ⚠️ <strong>Problème d'instances non publiées ?</strong> Cliquez ci-dessous pour publier toutes les instances.
                    </div>
                    <button
                      onClick={async () => {
                        if (!confirm('Publier toutes les instances non publiées ?\n\nCela mettra is_published: true sur toutes les instances de l\'API.')) {
                          return;
                        }
                        
                        setSyncInProgress(true);
                        setSyncResult(null);
                        
                        try {
                          // Importer le service
                          const { ModulesApiService } = await import('../Services/ModulesApiService');
                          const { ModuleInstancesApiService } = await import('../Services/ModuleInstancesApiService');
                          
                          const modulesApi = new ModulesApiService(config.api.baseUrl, config.api.readToken, config.api.writeToken);
                          const instancesApi = new ModuleInstancesApiService(config.api.baseUrl, config.api.readToken, config.api.writeToken);
                          
                          const allInstances = await instancesApi.getAllInstances();
                          let published = 0;
                          
                          for (const instance of allInstances) {
                            if (!instance.is_published) {
                              await instancesApi.updateInstance(instance.id, {
                                is_published: true
                              });
                              published++;
                            }
                          }
                          
                          setSyncResult(`✅ ${published} instance(s) publiée(s) !`);
                          alert(`✅ ${published} instance(s) publiée(s) !\n\nRechargez la page pour voir les changements.`);
                          
                        } catch (err) {
                          setSyncResult(`❌ Erreur : ${err instanceof Error ? err.message : String(err)}`);
                        } finally {
                          setSyncInProgress(false);
                        }
                      }}
                      disabled={syncInProgress || isLoading}
                      style={{
                        width: '100%',
                        padding: '8px',
                        backgroundColor: syncInProgress ? '#6c757d' : '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: syncInProgress ? 'not-allowed' : 'pointer',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        opacity: syncInProgress ? 0.6 : 1
                      }}
                    >
                      {syncInProgress ? '⏳ Publication...' : '📢 Publier Toutes les Instances'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Gestion des données */}
            {config.api.enableSync && (
              <div>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '14px' }}>🔄 Gestion des Données</h3>
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: 'rgba(255,255,255,0.05)', 
                  borderRadius: '6px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  marginBottom: '16px'
                }}>
                  <div style={{ fontSize: '10px', marginBottom: '12px', opacity: 0.8 }}>
                    Gérez le chargement et la synchronisation des données entre la config locale et l'API.
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <button
                      onClick={() => {
                        const success = resetToOriginalConfig(onConfigChange);
                        if (success) {
                          alert('✅ Configuration resetée à l\'état d\'origine !');
                          setSyncResult('Configuration locale restaurée. Les composants affichent maintenant les données d\'origine.');
                        } else {
                          alert('⚠️ Aucune configuration d\'origine trouvée. Rechargez la page pour sauvegarder la config actuelle.');
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '10px',
                        backgroundColor: '#ffc107',
                        color: '#000',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}
                    >
                      🔄 Reset à la Config Locale
                    </button>

                    <button
                      onClick={() => {
                        if (!confirm('📥 Charger depuis l\'API ?\n\nCela va écraser vos modifications locales avec les données de l\'API.')) {
                          return;
                        }
                        
                        // Effacer la sauvegarde locale
                        localStorage.removeItem('gandc_config_backup');
                        console.log('🗑️ Sauvegarde locale effacée');
                        
                        // Recharger la page pour forcer le chargement depuis l'API
                        window.location.reload();
                      }}
                      style={{
                        padding: '10px',
                        backgroundColor: '#17a2b8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}
                    >
                      📥 Charger depuis l'API
                    </button>

                    <button
                      onClick={async () => {
                        if (!confirm('⚠️ Resynchroniser TOUT depuis la config locale vers l\'API ?\n\nCela va écraser toutes les données de l\'API avec celles de la config locale.')) {
                          return;
                        }
                        
                        setSyncInProgress(true);
                        setSyncResult(null);
                        try {
                          await resyncFromLocal(config, syncInstances);
                          alert('✅ Resynchronisation terminée !');
                          setSyncResult('Toutes les instances ont été resynchronisées depuis la config locale vers l\'API.');
                          
                          // Recharger la page pour récupérer les nouvelles données
                          if (confirm('Recharger la page pour voir les changements ?')) {
                            window.location.reload();
                          }
                        } catch (err) {
                          alert('❌ Erreur lors de la resynchronisation');
                          setSyncResult(`❌ Erreur : ${err instanceof Error ? err.message : String(err)}`);
                        } finally {
                          setSyncInProgress(false);
                        }
                      }}
                      disabled={syncInProgress || isLoading}
                      style={{
                        width: '100%',
                        padding: '10px',
                        backgroundColor: syncInProgress ? '#6c757d' : '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: syncInProgress ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        opacity: syncInProgress ? 0.6 : 1
                      }}
                    >
                      {syncInProgress ? '⏳ Resynchronisation...' : '⚠️ Resync TOUT depuis Local → API'}
                    </button>
                  </div>

                  <div style={{ 
                    marginTop: '12px',
                    padding: '8px', 
                    backgroundColor: 'rgba(0, 123, 255, 0.1)', 
                    borderRadius: '4px',
                    fontSize: '9px',
                    opacity: 0.8
                  }}>
                    <div style={{ marginBottom: '4px', fontWeight: 'bold' }}>💡 Comment ça marche :</div>
                    <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
                      <li><strong>Au chargement :</strong> Les données de l'API sont chargées automatiquement</li>
                      <li><strong>Reset :</strong> Revient à la config locale (fichier config.ts)</li>
                      <li><strong>Resync :</strong> Envoie la config locale vers l'API (écrase l'API)</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Gestion des logs d'erreur */}
            <div>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '14px' }}>📋 Logs d'Erreur</h3>
              <div style={{ 
                padding: '12px', 
                backgroundColor: 'rgba(255,255,255,0.05)', 
                borderRadius: '6px',
                border: '1px solid rgba(255,255,255,0.1)'
              }}>
                <div style={{ fontSize: '10px', marginBottom: '12px', opacity: 0.8 }}>
                  Les erreurs de l'API sont automatiquement enregistrées. Vous pouvez les consulter ou les télécharger.
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => {
                      const errors = ModulesApiService.getStoredErrors();
                      alert(`📋 ${errors.length} erreur(s) enregistrée(s)\n\nConsultez la console pour plus de détails.`);
                      console.log('Erreurs stockées:', errors);
                    }}
                    style={{
                      flex: 1,
                      padding: '8px',
                      backgroundColor: '#17a2b8',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: 'bold'
                    }}
                  >
                    📋 Consulter les Logs
                  </button>

                  <button
                    onClick={() => {
                      ModulesApiService.downloadErrorsAsFile();
                    }}
                    style={{
                      flex: 1,
                      padding: '8px',
                      backgroundColor: '#ffc107',
                      color: '#000',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: 'bold'
                    }}
                  >
                    💾 Télécharger les Logs
                  </button>

                  <button
                    onClick={() => {
                      if (confirm('⚠️ Effacer tous les logs d\'erreur ?')) {
                        ModulesApiService.clearStoredErrors();
                        alert('✅ Logs effacés !');
                      }
                    }}
                    style={{
                      padding: '8px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: 'bold'
                    }}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>

            {/* Informations sur l'état de l'API */}
            <div style={{
              padding: '10px',
              backgroundColor: config.api.enableSync ? 'rgba(40, 167, 69, 0.1)' : 'rgba(255, 193, 7, 0.1)',
              borderRadius: '4px',
              border: `1px solid ${config.api.enableSync ? 'rgba(40, 167, 69, 0.3)' : 'rgba(255, 193, 7, 0.3)'}`,
              fontSize: '10px'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                {config.api.enableSync ? '✅ Synchronisation activée' : '⚠️ Synchronisation désactivée'}
              </div>
              <div style={{ opacity: 0.8 }}>
                {config.api.enableSync 
                  ? 'Les modules peuvent être synchronisés avec l\'API.' 
                  : 'Activez la synchronisation pour utiliser les fonctionnalités de l\'API.'}
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Footer simplifié */}
      <div style={{ 
        padding: '12px 16px', 
        borderTop: '1px solid rgba(255,255,255,0.1)',
        backgroundColor: 'rgba(0,0,0,0.2)',
        display: 'flex',
        gap: '8px'
      }}>
          <button
            onClick={() => {
              try {
                localStorage.setItem('gandc_config_backup', JSON.stringify(config));
              alert('✅ Configuration sauvegardée !');
              } catch (error) {
              alert('❌ Erreur : ' + error);
              }
            }}
            style={{
              flex: 1,
            padding: '8px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
            borderRadius: '4px',
              cursor: 'pointer',
            fontSize: '11px',
              fontWeight: 'bold'
            }}
          >
            💾 Sauvegarder
          </button>

          <button
            onClick={() => {
            // Exporter la configuration
            const configCode = `const config = ${JSON.stringify(config, null, 2)};`;
            navigator.clipboard.writeText(configCode);
            alert('✅ Config copiée !');
            }}
            style={{
              flex: 1,
            padding: '8px',
            backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
            borderRadius: '4px',
              cursor: 'pointer',
            fontSize: '11px',
              fontWeight: 'bold'
            }}
          >
          📋 Exporter
          </button>

        <button
          onClick={() => setShowAdvancedSave(!showAdvancedSave)}
          style={{
            padding: '8px 12px',
            backgroundColor: showAdvancedSave ? '#dc3545' : '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '11px',
            fontWeight: 'bold'
          }}
        >
          {showAdvancedSave ? '✕' : '⚙️'}
        </button>
      </div>

      {/* Fonctions avancées (masquées par défaut) */}
      {showAdvancedSave && (
        <div style={{ 
          padding: '12px 16px', 
          borderTop: '1px solid rgba(255,255,255,0.1)',
          backgroundColor: 'rgba(0,0,0,0.4)',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px'
        }}>
        <button
          onClick={() => {
              try {
                const savedConfig = localStorage.getItem('gandc_config_backup');
                if (savedConfig) {
                  if (confirm('Charger la sauvegarde ?')) {
                    const parsedConfig = JSON.parse(savedConfig);
                    onConfigChange(parsedConfig);
                    alert('✅ Config chargée !');
                  }
            } else {
                  alert('❌ Aucune sauvegarde');
                }
              } catch (error) {
                alert('❌ Erreur : ' + error);
            }
          }}
          style={{
            width: '100%',
              padding: '6px',
            backgroundColor: '#17a2b8',
            color: 'white',
            border: 'none',
              borderRadius: '4px',
            cursor: 'pointer',
              fontSize: '10px'
          }}
        >
            📁 Charger Sauvegarde
        </button>

        <button
          onClick={() => {
              if (confirm('⚠️ Reset complet ?')) {
                try {
                  localStorage.removeItem('gandc_config_backup');
                  window.location.reload();
                } catch (error) {
                  alert('❌ Erreur : ' + error);
              }
            }
          }}
          style={{
            width: '100%',
              padding: '6px',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
              borderRadius: '4px',
            cursor: 'pointer',
              fontSize: '10px'
          }}
        >
            🔄 Reset Complet
        </button>
        </div>
      )}

      {/* Modale de sélection de composant */}
      {showComponentModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 20000
        }}>
          <div style={{
            width: '80%',
            maxWidth: '800px',
            maxHeight: '80%',
            backgroundColor: 'rgba(20, 20, 20, 0.98)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Header de la modale */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: 'white' }}>
                Choisir un Composant
              </h2>
              <button
                onClick={() => setShowComponentModal(false)}
                style={{
                  padding: '8px 12px',
                  backgroundColor: 'rgba(220, 53, 69, 0.8)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                ✕
              </button>
            </div>

            {/* Barre de recherche et filtres */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              backgroundColor: 'rgba(0, 0, 0, 0.2)'
            }}>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                <div style={{ flex: 1 }}>
                  <input
                    type="text"
                    placeholder="Rechercher un composant..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      width: '90%',
                      padding: '10px 12px',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  style={{
                    padding: '10px 12px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    minWidth: '150px'
                  }}
                >
                  <option value="all" style={{ backgroundColor: '#333', color: 'white' }}>Toutes les catégories</option>
                  {Array.from(new Set(getAllComponents().map(c => c.category))).map(category => (
                    <option key={category} value={category} style={{ backgroundColor: '#333', color: 'white' }}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Liste des composants */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px 20px'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                gap: '16px'
              }}>
                {getAllComponents()
                  .filter(component => {
                    const matchesSearch = component.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                        component.description.toLowerCase().includes(searchTerm.toLowerCase());
                    const matchesCategory = selectedCategory === 'all' || component.category === selectedCategory;
                    return matchesSearch && matchesCategory;
                  })
                  .map((metadata) => (
                    <div 
                      key={metadata.name}
                      style={{ 
                        padding: '16px', 
                        backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(0, 123, 255, 0.1)';
                        e.currentTarget.style.borderColor = 'rgba(0, 123, 255, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                      }}
                    >
                      {/* Image du composant */}
                      <div style={{
                        width: '100%',
                        height: '120px',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '6px',
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '48px',
                        color: 'rgba(255, 255, 255, 0.3)',
                        backgroundImage: `url(/src/Module/${metadata.name}/preview.png)`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat'
                      }}>
                        {/* Fallback si pas d'image */}
                        <div style={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(0, 0, 0, 0.3)',
                          borderRadius: '6px',
                          fontSize: '32px',
                          color: 'rgba(255, 255, 255, 0.5)'
                        }}>
                          {metadata.category === 'Navigation' ? '🧭' : 
                           metadata.category === 'Content' ? '📄' : 
                           metadata.category === 'Layout' ? '📐' : 
                           metadata.category === 'UI' ? '🎨' : '🔧'}
                        </div>
                      </div>

                      {/* Informations du composant */}
                      <div style={{ marginBottom: '12px' }}>
                        <h3 style={{ 
                          margin: '0 0 6px 0', 
                          fontSize: '16px', 
                          fontWeight: 'bold',
                          color: 'white'
                        }}>
                          {metadata.displayName}
                        </h3>
                        <p style={{ 
                          margin: '0 0 8px 0', 
                          fontSize: '12px', 
                          opacity: 0.7,
                          color: 'white',
                          lineHeight: '1.4'
                        }}>
                          {metadata.description}
                        </p>
                        <div style={{ 
                          fontSize: '10px', 
                          opacity: 0.6,
                          padding: '4px 8px',
                          backgroundColor: 'rgba(0, 123, 255, 0.2)',
                          borderRadius: '4px',
                          display: 'inline-block',
                          color: '#007bff',
                          fontWeight: 'bold'
                        }}>
                          {metadata.category}
                        </div>
                      </div>

                      {/* Bouton d'ajout */}
                      <button
                        onClick={() => {
                          const newId = `${metadata.name}-${Date.now()}`;
                          const newItem: LayoutItem = {
                            id: newId,
                            component: metadata.name,
                            enabled: true,
                            outsideGrid: metadata.outsideGrid,
                            page: currentPageUrl,
                            props: { ...metadata.defaultParams },
                            positions: {}
                          };

                          if (currentPage?.gridLayout) {
                            const newPageItems = [...currentPage.gridLayout.items, newItem];
                            const newPages = config.PagesList.map(p => 
                              p.pageUrl === currentPageUrl 
                                ? { ...p, gridLayout: { ...p.gridLayout, items: newPageItems, breakpoints: p.gridLayout?.breakpoints ?? config.gridLayout.breakpoints } }
                                : p
                            );
                            onConfigChange({ PagesList: newPages });
                          } else {
                            const newItems = [...config.gridLayout.items, newItem];
                            onConfigChange({ gridLayout: { ...config.gridLayout, items: newItems } });
                          }

                          // Activer le module
                          const newModules = { ...config.modules, [metadata.name]: true };
                          const newParams = {
                            ...config.componentParams,
                            [metadata.name]: metadata.defaultParams
                          };
                          onConfigChange({ modules: newModules, componentParams: newParams });

                          // Sélectionner pour placement
                          if (onSelectComponentForMove) {
                            onSelectComponentForMove(newId);
                          }
                          setShowComponentModal(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#218838';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#28a745';
                        }}
                      >
                        + Ajouter ce Composant
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfigEditor;
